<template>
  <footer class="app-footer">
    <!-- Newsletter Section - Top of Footer -->
    <div class="newsletter-section">
      <div class="newsletter-container">
        <div class="newsletter-content">
          <div class="newsletter-text-wrapper">
            <h2 class="newsletter-heading">Newsletter</h2>
            <p>Subscribe to get updates on new arrivals and exclusive offers.</p>
          </div>
          <form @submit.prevent="handleNewsletter" class="newsletter-inline-form">
            <input
              v-model="newsletter.first_name"
              type="text"
              placeholder="First Name"
              required
              class="newsletter-input"
            >
            <input
              v-model="newsletter.last_name"
              type="text"
              placeholder="Last Name"
              required
              class="newsletter-input"
            >
            <input
              v-model="newsletter.email"
              type="email"
              placeholder="Email Address"
              required
              class="newsletter-input email-input"
            >
            <button type="submit" class="newsletter-btn" :disabled="newsletterLoading">
              <i v-if="newsletterLoading" class="fas fa-spinner fa-spin"></i>
              <span v-else>Subscribe</span>
            </button>
          </form>
        </div>
        <p v-if="newsletterMessage" :class="['newsletter-message', newsletterSuccess ? 'success' : 'error']">
          {{ newsletterMessage }}
        </p>
      </div>
    </div>

    <div class="container">
      <!-- Footer Links -->
      <div class="footer-links-wrapper">

        <div class="footer-column">
          <h3 @click="toggleSection('about')" class="footer-toggle-heading">
            Ibomstore
            <i class="fas" :class="openSections.about ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <ul :class="{ 'is-open': openSections.about }">
            <li><a :href="route('about')">About Us</a></li>
            <li><a :href="route('terms')">Terms & Conditions</a></li>
            <li><a :href="route('policies')">Privacy Policy</a></li>
          </ul>
        </div>

        <div class="footer-column">
          <h3 @click="toggleSection('service')" class="footer-toggle-heading">
            Customers
            <i class="fas" :class="openSections.service ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <ul :class="{ 'is-open': openSections.service }">
            <li><a :href="route('help-center')">Help Center</a></li>
            <li><a :href="route('contact')">Contact Us</a></li>
            <li><a :href="route('tracking')">Track Order</a></li>
            <li><a :href="route('refund')">Return Policy</a></li>
            <li><a :href="route('help-center')">FAQs</a></li>
          </ul>
        </div>

        <div class="footer-column">
          <h3 @click="toggleSection('shop')" class="footer-toggle-heading">
            Shopping
            <i class="fas" :class="openSections.shop ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <ul :class="{ 'is-open': openSections.shop }">
            <li><a :href="route('products.index')">All Products</a></li>
            <li><a :href="route('trending')">Trending</a></li>
            <li><a :href="route('today-deals')">Today's Deals</a></li>
            <li><a :href="route('flash-sales')">Flash Sales</a></li>
          </ul>
        </div>

        <div class="footer-column">
          <h3 @click="toggleSection('follow')" class="footer-toggle-heading">
            Media
            <i class="fas" :class="openSections.follow ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <div class="footer-content" :class="{ 'is-open': openSections.follow }">
            <div class="footer-social">
              <a href="https://facebook.com/ibomstore" target="_blank" aria-label="Facebook"><i class="fab fa-facebook-f"></i></a>
              <a href="https://instagram.com/ibomstore" target="_blank" aria-label="Instagram"><i class="fab fa-instagram"></i></a>
              <a href="https://twitter.com/@ibomstore" target="_blank" aria-label="Twitter"><i class="fab fa-twitter"></i></a>
              <a href="https://youtube.com/@ibomstore" target="_blank" aria-label="YouTube"><i class="fab fa-youtube"></i></a>
              <a href="https://wa.me/2347011110034" target="_blank" aria-label="WhatsApp"><i class="fab fa-whatsapp"></i></a>
            </div>
            <h4 class="payment-heading">Payment Methods</h4>
            <div class="payment-methods">
              <i class="fab fa-cc-visa"></i>
              <i class="fab fa-cc-mastercard"></i>
            </div>
          </div>
        </div>

      </div>

      <!-- Copyright Bar -->
      <div class="footer-copyright">
        <p>Copyright © {{ currentYear }} {{ props.appName.toUpperCase() }}. All Rights Reserved.
          <a :href="route('terms')">User Agreement</a>,
          <a :href="route('policies')">Privacy</a> and
          <a :href="route('policies')">Cookies</a>
        </p>
      </div>

    </div>
  </footer>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'

// Props from Laravel
const props = defineProps({
  appName: {
    type: String,
    default: 'Ibomstore'
  },
  appUrl: {
    type: String,
    default: '/'
  }
})

const currentYear = computed(() => new Date().getFullYear())
const logoUrl = computed(() => '/logos/logo.svg')

// Newsletter form state
const newsletter = reactive({
  first_name: '',
  last_name: '',
  email: ''
})
const newsletterLoading = ref(false)
const newsletterMessage = ref('')
const newsletterSuccess = ref(false)

// Toggle state for mobile footer sections
const openSections = reactive({
  about: false,
  service: false,
  shop: false,
  follow: false,
  newsletter: false
})

const toggleSection = (section) => {
  openSections[section] = !openSections[section]
}

const route = (name) => {
  const routes = {
    'home': '/',
    'products.index': '/products',
    'collections.index': '/collections',
    'brands.index': '/brands',
    'cart.view': '/cart',
    'dashboard': '/dashboard',
    'orders.index': '/orders',
    'about': '/about',
    'contact': '/contact',
    'help-center': '/help-center',
    'refund': '/refund',
    'trending': '/trending',
    'today-deals': '/today-deals',
    'flash-sales': '/flash-sales',
    'clearance': '/clearance',
    'policies': '/policies',
    'terms': '/terms',
    'tracking': '/track'
  }
  return routes[name] || '/'
}

const handleNewsletter = async () => {
  if (!newsletter.first_name || !newsletter.last_name || !newsletter.email) {
    return
  }

  newsletterLoading.value = true
  newsletterMessage.value = ''
  newsletterSuccess.value = false

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch('/newsletter/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        first_name: newsletter.first_name,
        last_name: newsletter.last_name,
        email: newsletter.email
      })
    })

    const data = await response.json()

    if (response.ok) {
      newsletterSuccess.value = true
      newsletterMessage.value = data.message || 'Successfully subscribed!'
      newsletter.first_name = ''
      newsletter.last_name = ''
      newsletter.email = ''
      setTimeout(() => { newsletterMessage.value = '' }, 5000)
    } else {
      newsletterSuccess.value = false
      newsletterMessage.value = data.message || data.errors?.email?.[0] || 'Failed to subscribe. Please try again.'
    }
  } catch (error) {
    newsletterSuccess.value = false
    newsletterMessage.value = 'An error occurred. Please try again.'
  } finally {
    newsletterLoading.value = false
  }
}
</script>
